/**
 * Whether the account's plan lets a template use a File column, for the
 * editors' Add column menu.
 *
 * Read from GET /files/usage when an editor opens: the server asks the hub
 * (cached two minutes per account), so an upgrade shows on the next open
 * without a reload. Only a plan the server read and that includes files makes
 * the column available; while checking, on a plan without files, and when the
 * plan could not be read, the File column sits in the "Not supported" group.
 */
import { useEffect, useState } from 'react';

export const FILES_PLAN = Object.freeze({
  CHECKING: 'checking',
  AVAILABLE: 'available',
  NOT_IN_PLAN: 'not_in_plan',
  UNKNOWN: 'unknown',
});

/** The /files/usage answer → one of FILES_PLAN. */
export function filesPlanFrom(usage) {
  if (!usage || usage.error || !usage.plan) return FILES_PLAN.UNKNOWN;
  return usage.plan.supportsFiles ? FILES_PLAN.AVAILABLE : FILES_PLAN.NOT_IN_PLAN;
}

/** Why the File column cannot be added, or null when it can. */
export function fileColumnBlock(status) {
  switch (status) {
    case FILES_PLAN.AVAILABLE:
      return null;
    case FILES_PLAN.NOT_IN_PLAN:
      return 'Not in your plan';
    case FILES_PLAN.CHECKING:
      return 'Checking your plan';
    default:
      return 'Could not check your plan';
  }
}

/** FILES_PLAN for the editor's lifetime; `service` is the lib/files service. */
export function useFilesPlan(service) {
  const [status, setStatus] = useState(FILES_PLAN.CHECKING);
  useEffect(() => {
    let alive = true;
    if (!service) {
      setStatus(FILES_PLAN.UNKNOWN);
      return undefined;
    }
    Promise.resolve()
      .then(() => service.usage())
      .then((u) => alive && setStatus(filesPlanFrom(u)))
      .catch(() => alive && setStatus(FILES_PLAN.UNKNOWN));
    return () => {
      alive = false;
    };
  }, [service]);
  return status;
}
